import { toast } from 'sonner';
import { apiErrorMessage } from './api';

// Single user-feedback choke point (shadcn's Sonner under the hood). Components call these instead
// of `toast` directly so styling and API-error normalisation live in one place.
// See CLAUDE.md §4 (User feedback / toasts).

// Every successful action (submit, confirm-style click, copy, …) reports through this.
export function notifySuccess(message: string): void {
  toast.success(message);
}

// Non-blocking caution that isn't an outright failure (e.g. "nothing to save", soft limits).
export function notifyWarning(message: string): void {
  toast.warning(message);
}

// Every failed action reports through this. Accepts a ready-made string or any thrown value —
// API errors (the `{ message }` envelope / axios) are normalised into a readable line.
export function notifyError(error: unknown, fallback = 'Something went wrong'): void {
  const message = typeof error === 'string' ? error : apiErrorMessage(error, fallback);
  toast.error(message);
}

interface PromiseMessages<T> {
  loading: string;
  // A static line or a builder from the resolved value (e.g. `(fed) => \`${fed.name} created\``).
  success: string | ((data: T) => string);
  // Optional override; otherwise the thrown API error is normalised like notifyError.
  error?: string;
}

// Wraps an in-flight promise in a single toast that swaps loading → success/error. Returns the
// same promise so callers can still await the result.
export function notifyPromise<T>(promise: Promise<T>, messages: PromiseMessages<T>): Promise<T> {
  toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: (err: unknown) =>
      messages.error ?? apiErrorMessage(err, 'Something went wrong'),
  });
  return promise;
}
